import { Component, OnInit, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, delay, filter, map, of, retry, Subscription, switchMap, tap, throwError } from 'rxjs';
import { Event, NavigationEnd, Router, Scroll } from '@angular/router';

import { Store } from '@ngrx/store';
import * as InputsStore from '@app/shared/store/inputs';
import * as ShippingPricesStore from '@app/shared/store/prices';
import * as ParcelShopStore from '@app/parcelshops/store/store/parcelshops';
import { environment } from '@app-env/environment';
import { SiteKey } from './core/models';
import { Meta, Title } from '@angular/platform-browser';
import { AnalyticsService } from '@app/shared/services/analytics';
import { PromotionModalService } from '@app/shared/modals/promotion/promotion.service';

@Component({
	selector: 'gls-app',
	templateUrl: 'app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
	public SITE_CONFIG = environment.SITE_CONFIG as SiteKey;

	readonly HEADER_OFFSET = 0;
	readonly SCROLL_DELAY = 500;
	readonly SCROLL_RETRY = 5;

	private _subscriptions: Subscription[] = [];
	// public readonly isECsomag$ = new BehaviorSubject(false);

	constructor(
		@Inject(PLATFORM_ID) private platformId: object,
		@Inject(DOCUMENT) private document: any,
		private translate: TranslateService,
		private _location: Location,
		private _router: Router,
		private _uiState: Store<InputsStore.State>,
		private title: Title,
		private meta: Meta,
		private analytics: AnalyticsService,
		private promotionModal: PromotionModalService,
		private shippingPricesStore: Store<ShippingPricesStore.State>,
		private parcelShopStore: Store<ParcelShopStore.State>
	) {
		const locales = ['en', 'hu'];
		let lang = this.translate.getDefaultLang();
		const locationPath = this._location.path() || '/hu';
		const pathSlices = locationPath
			.split(/(?=\()/)[0]
			.split('#')[0]
			.split('?')[0]
			.split('/');
		if (pathSlices.length > 1 && locales.indexOf(pathSlices[1]) !== -1) {
			lang = pathSlices[1];
		}
		this.translate.use(lang);
		this._uiState.dispatch(InputsStore.actions.setLanguage({ language: lang }));

		if (this.SITE_CONFIG === 'ecsomag-hu') {
			this._uiState.dispatch(InputsStore.actions.setProp({ key: 'pageLoaded', value: true }));
		}
	}

	public showPromotion(): void {
		this.promotionModal.open();
	}

	private _addPassiveTouchStartListenerToBody(): void {
		// Test via a getter in the options object to see if the passive property is accessed
		let supportsPassive = false;
		try {
			const opts = Object.defineProperty({}, 'passive', {
				get: () => {
					supportsPassive = true;
				},
			}) as AddEventListenerOptions;
			window.addEventListener('testPassive', () => void 0, opts);
			window.removeEventListener('testPassive', () => void 0, opts);
		} catch (e) {
			//
		}
		// Use our detect's results. passive applied if supported, capture will be false either way.
		this.document.body.addEventListener('touchstart', () => void 0, supportsPassive ? { passive: true } : false);
	}

	// private _addGTM(): void {
	// 	let GTM: string;
	// 	switch (this.SITE_CONFIG) {
	// 		case 'csomag-hu':
	// 			GTM = '5Q838VV';
	// 			break;
	// 		case 'ecsomag-hu':
	// 			GTM = 'TPT3V78';
	// 			break;
	// 	}

	// 	if (GTM) {
	// 		const nonce = this.document.querySelector('meta[name="CSP-NONCE"]')?.getAttribute('content');
	// 		const gtmEl = this.document.createElement('script');
	// 		if (nonce) gtmEl.setAttribute('nonce', nonce);
	// 		gtmEl.appendChild(
	// 			this.document.createTextNode(`
	// 				function addScripts() {
	// 					(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
	// 					new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
	// 					j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
	// 					'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
	// 					n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f);
	// 					})(window,document,'script','dataLayer','GTM-${GTM}');
	// 				}

	// 				if ('requestIdleCallback' in window) {
	// 					window.requestIdleCallback(addScripts);
	// 				} else {
	// 					addScripts();
	// 				}
	// 			`)
	// 		);
	// 		this.document.body.appendChild(gtmEl);
	// 	}
	// }

	private _addOneTrust(): void {
		let hash: string;
		switch (this.SITE_CONFIG) {
			case 'csomag-hu':
				hash = '9594e92e-2c03-490c-81c4-baa12f0ba1d7';
				break;
			case 'ecsomag-hu':
				hash = '8b1db776-96c7-43ec-8e4c-223e5b8c7061';
				break;
		}

		if (hash) {
			const nonce = this.document.querySelector('meta[name="CSP-NONCE"]')?.getAttribute('content');

			const oneTrustEl1 = this.document.createElement('script');
			if (nonce) oneTrustEl1.setAttribute('nonce', nonce);
			oneTrustEl1.setAttribute('type', 'text/javascript');
			oneTrustEl1.setAttribute('src', `https://cdn.cookielaw.org/consent/${hash}/OtAutoBlock.js`);
			this.document.body.appendChild(oneTrustEl1);

			const oneTrustEl2 = this.document.createElement('script');
			if (nonce) oneTrustEl2.setAttribute('nonce', nonce);
			oneTrustEl2.setAttribute('type', 'text/javascript');
			oneTrustEl2.setAttribute('src', 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js');
			oneTrustEl2.setAttribute('charset', 'UTF-8');
			oneTrustEl2.setAttribute('data-domain-script', hash);
			this.document.body.appendChild(oneTrustEl2);

			const oneTrustEl3 = this.document.createElement('script');
			if (nonce) oneTrustEl3.setAttribute('nonce', nonce);
			oneTrustEl3.setAttribute('type', 'text/javascript');
			oneTrustEl3.appendChild(this.document.createTextNode('function OptanonWrapper() { }'));
			this.document.body.appendChild(oneTrustEl3);
		}
	}

	private _addDPM(): void {
		if (environment.DPM_URL) {
			const nonce = this.document.querySelector('meta[name="CSP-NONCE"]')?.getAttribute('content');

			const dpmScriptEl = this.document.createElement('script');
			if (nonce) dpmScriptEl.setAttribute('nonce', nonce);
			dpmScriptEl.setAttribute('type', 'module');
			dpmScriptEl.setAttribute('src', environment.DPM_URL);
			this.document.body.appendChild(dpmScriptEl);
		}
	}

	// private _setTitle(): void {
	// 	let title: string;
	// 	switch (this.SITE_CONFIG) {
	// 		case 'csomag-hu':
	// 			title = 'GLS CsomagPont';
	// 			break;
	// 		case 'ecsomag-hu':
	// 			title = 'GLS eCsomag';
	// 			break;
	// 	}

	// 	if (title) {
	// 		this.title.setTitle(title);
	// 	}
	// }

	ngOnInit(): void {
		// console.log('app component oninit');
		this.shippingPricesStore.dispatch(ShippingPricesStore.actions.load());

		if (isPlatformBrowser(this.platformId)) {
			this._addPassiveTouchStartListenerToBody();
			this._addDPM();
			if (environment.APP_ENV !== 'develop') {
				// this._addGTM();
				this._addOneTrust();
			}
			// this._setTitle();

			// @TODO: https://quandh19.medium.com/how-to-enable-anchor-scrolling-of-angular-router-in-the-right-way-42e9b19657b5

			// https://github.com/angular/angular/issues/30139#issuecomment-706321919
			this._subscriptions.push(
				this._router.events
					.pipe(
						filter(event => event instanceof Scroll && typeof event.anchor === 'string'),
						tap(event => {
							console.log(event);
						}),
						map(event => (event as Scroll).anchor),
						switchMap(anchor =>
							of(anchor).pipe(
								delay(this.SCROLL_DELAY),
								tap(anchor => {
									const targetElement = document.querySelector('#' + anchor);
									// console.log('router scroll check for anchor', anchor, targetElement);
									if (!targetElement) {
										throw 'no target';
									} else {
										// console.log('router scroll to', anchor, isInViewport(targetElement))
										// if (!isInViewport(targetElement)) {
										// 	targetElement.scrollIntoView(true);
										// }

										const bounding = targetElement.getBoundingClientRect();
										// console.log(bounding, window.innerHeight, window.scrollY);
										window.scrollTo({ top: window.scrollY + bounding.top });
									}
								}),
								retry(this.SCROLL_RETRY),
								catchError(e => of())
							)
						)
					)
					.subscribe(anchor => {
						console.log('router scroll event', anchor);
						// setTimeout(() => {
						// const targetElement = document.querySelector('#' + anchor);
						// 	console.log('router scroll event timeout', anchor, targetElement)
						// 	if (!targetElement) {
						// 		console.log('router scroll to 0')
						// 		window.scrollTo(0, 0);
						// 	} else {
						// 	}
						// }, this.SCROLL_DELAY);
					})
			);

			// this.showPromotion();
		}
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach(subscription => subscription.unsubscribe());
	}
}

// https://gomakethings.com/how-to-test-if-an-element-is-in-the-viewport-with-vanilla-javascript/
const isInViewport = (elem: Element) => {
	const bounding = elem.getBoundingClientRect();
	return (
		bounding.top >= 0 &&
		bounding.left >= 0 &&
		bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
		bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
};
